import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Eye, Loader2, Search, Download, FileText, CheckCircle2, RefreshCw, AlertTriangle, Info, X, ChevronDown, ArrowUp, ArrowDown, CalendarDays, Landmark } from 'lucide-react'
import { apiGet, cachedGet, postJson } from '../../lib/api.js'
import { canSetClaimStatus, canWriteOffClaim } from '../../lib/role.js'
import PatientLink from '../../components/PatientLink.jsx'
import { useMe } from '../../lib/me.js'
import { useXanoRealtime } from '../../lib/realtime.js'
import { asList } from '../../lib/chart-helpers.js'
import { usePersistedState, useEphemeralState } from '../../lib/persist.js'
import { nameInitial } from '../../lib/text.js'
import { Card, Notice, DateRange, fmt, toISO, parseISO, todayISO, INPUT, TH_MON_ABBR } from '../statistic/statShared.jsx'
import { toISODate, dateParts } from '../../kit/format.js'
import { PAYER_GROUPS, payerGroupKey } from '../../data/reimbursementPayers.js'
import Pager from '../../components/Pager.jsx'

/* -------------------------------------------------------------------------- */
/* รายการตามเบิก (reimbursement / advance-pay claims) — money owed to us by       */
/* third-party payers (สปสช./ปกส./insurers/platforms), not collected from the     */
/* patient. Purpose = accounting reconciliation: who owes us, how much, which     */
/* claims — and marking claims รับแล้ว once the payer pays (bulk-mark).            */
/*                                                                                */
/* HOW IT LOADS — SUMMARY FIRST, LINES ON DEMAND.                                  */
/*  • /api/billing/reimbursement-summary (Xano api 639) → the whole สรุปยอดตามผู้จ่าย */
/*    table in ONE aggregate: {totals:[{label,status,lines,advance,received_amount}],*/
/*    scope:[{label,patient_id,treatment_id}]}. `totals` is the AUTHORITATIVE money  */
/*    — every headline baht is a SQL SUM, never a re-sum of rows in this file.       */
/*  • /api/billing/reimbursement-lines (Xano api 640) → claim lines for ONE payer,   */
/*    fetched only when you pick that payer (or press โหลดรายการทั้งหมด).             */
/*                                                                                */
/* ⚠️ WHY, because reverting this looks harmless and is not. Until 2026-08-26 the    */
/* page derived every figure in the browser, so it downloaded EVERY line of EVERY   */
/* payer before painting one number: measured on the live Worker 2026-08-25 over    */
/* 2026-05 / all branches, 13 requests and 13–22s wall clock, of which two single    */
/* pages (สปสช. and ปกส.) were 9.4s each. That was not the query — api 179          */
/* type=reimbursement hangs SIX addons off every row and this page read one of them. */
/* The same window through api 639 answers in ~0.2s. Do not go back to summing      */
/* lines for the summary; the lines may not even be loaded.                          */
/*                                                                                */
/*  • api 179 type=reimbursement / _dashboard / _total / _csv still exist and still  */
/*    serve the receipt generator and the importers. This page no longer calls them. */
/*                                                                                */
/* Lines carry branch_id/doctor_id (NOT names → resolved via /api/branches +       */
/* /api/doctors maps) and an epoch-ms `date`. Insurance direct-claim lines are     */
/* "เบิกคืนบริษัท X" reversals: priceXunit is NEGATIVE, priceadvance is the claim,  */
/* priceActualTotal=priceadvance, with a claimRef. So เบิก (priceadvance) is the    */
/* headline column here; เก็บเอง is intentionally omitted (it's a reversal art).    */
/*                                                                                */
/* Reconciliation status (advance_status/advance_received_at/advance_received_by,   */
/* written via PATCH /billing/{billing_id}) is a Xano change — see                  */
/* docs/reimbursement-xano-spec.md. Until it lands everything reads ค้างรับ and the */
/* bulk button surfaces a friendly notice.                                          */
/* -------------------------------------------------------------------------- */

const INDIGO = '#6366f1'
/* ×500 = 3000 lines per payer per range — far above real volume (ปกส., the biggest
   payer, is ~1,350 lines a month). A payer that ever exceeds it loads short, and the
   completeness check under the filters says so rather than quietly under-reporting. */
const MAX_PAGES_PER_PAYER = 6

/* ── How long a summary re-read waits before it goes out, by what asked for it ──────
   ⚠️ MEASURED ON THE LIVE INSTANCE 2026-09-14, and the reason these are not zero.
   api 639 answers a 2-month / all-branch window in 0.2–0.3s when it is the only caller
   and in 18–21s when three tabs ask inside the same second. It is not a missing index
   (billing.date and billing.advancePay are both btree-indexed) — it is two aggregate
   scans of `billing` running concurrently. And the page's own realtime handler was the
   thing making them concurrent: one broadcast woke EVERY open tab and each fired the
   aggregate in the same millisecond, so one colleague ticking one line cost everybody
   else a 20-second wait. These delays collapse a burst into one request, and the
   jitter keeps N tabs from arriving together. */
const REFRESH_OWN_MS = 700       // our own write — the rows are already patched, the totals follow
const REFRESH_HIT_MS = 2500      // someone else changed a line we are actually showing
const REFRESH_IDLE_MS = 25000    // ...a line we are NOT showing: still converge, but lazily
const REFRESH_JITTER_MS = 1500   // spread the tabs one broadcast wakes

// ── money / field accessors (billing line schema, probed live) ──
const num = (v) => { const n = Number(String(v ?? '').replace(/[, ฿]/g, '')); return isNaN(n) ? 0 : n }
const bAdvance = (b) => num(b.priceadvance ?? b.priceAdvance ?? 0)            // เบิก (claim amount)
const bClinic = (b) => num(b.priceActualClinic ?? b.priceClinic ?? 0)        // คลินิก share
const bDoctor = (b) => num(b.priceDoctor ?? 0)                               // หมอ share
const bTotal = (b) => { const t = b.priceActualTotal; return t != null ? num(t) : bClinic(b) + bDoctor(b) }
const bClaimRef = (b) => String(b.claimRef ?? b.claim_ref ?? '').trim()
const bPayer = (b) => String(b.advancePay ?? b.advancepay ?? '').trim() || 'ไม่ระบุผู้จ่าย'
const bIsActive = (b) => b.active !== false
/* ⚠️ FOUR STATES since 2026-08-24, and this used to collapse everything that was not
   `received` into `pending`. That silently counted written-off money as still owed and
   showed a part-paid claim as though the payer had sent nothing. Mirrors
   src/lib/billing.js:bClaimStatus — change one, change the other.
     ""        no claim / never reconciled       → treated as pending, as before
     pending   ค้างรับ (includes "insurer refused but we are still arguing")
     partial   paid less than claimed, CLOSED at that amount
     rejected  written off by a human. NEVER produced by any importer. */
const CLAIM_STATES = new Set(['pending', 'received', 'partial', 'rejected'])
const bStatus = (b) => {
  const s = String(b.advance_status ?? '').toLowerCase()
  return CLAIM_STATES.has(s) ? s : 'pending'
}
// What the payer actually sent. `received` is the full claim by definition.
const bGot = (b) => {
  const st = bStatus(b)
  if (st === 'received') return bAdvance(b)
  if (st !== 'partial') return 0
  return num(b.advance_received_amount)
}
/* ⚠️ THE ONLY FIGURE ค้างรับ MAY BE SUMMED FROM. `rejected` is money the clinic has
   given up on — leaving it in keeps dead claims on the books forever, which is the
   one thing a write-off exists to stop. `partial` closed at the settled amount. */
const bOwed = (b) => (bStatus(b) === 'pending' ? bAdvance(b) : 0)
const bShortfall = (b) => (bStatus(b) === 'partial' ? Math.max(0, bAdvance(b) - bGot(b)) : 0)
const bReceivedAt = (b) => b.advance_received_at ?? null
// received_at may come back as epoch-ms (Xano) or an ISO string (optimistic) → 'YYYY-MM-DD'
const clock = (t) => new Date(t).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })
const dayISO = (v) => {
  if (v == null || v === '') return ''
  // epoch → the Bangkok day (toISODate), not the browser's; it takes s or ms
  if (typeof v === 'number' || /^\d{10,}$/.test(String(v))) return toISODate(v)
  return String(v).slice(0, 10)
}
/* api 640 evals the billing_type name flat off a JOIN (`billing_type_name`) instead of
   paying for an addon per row. The nested-object form is api 179's shape and is kept as
   a fallback so this reader still works if anything is ever fed from that endpoint. */
const bTypeName = (b) => {
  const flat = String(b.billing_type_name ?? '').trim()
  if (flat) return flat
  const bt = b.billing_type ?? b.billingType
  if (bt && typeof bt === 'object') return String(bt.name || bt.type || bt.detail || '').trim()
  return String(bt ?? b.detail ?? '').trim() || '—'
}
const stripStars = (s) => String(s ?? '').replace(/^\**|\**$/g, '').trim()
// `date` is epoch-ms (number or numeric string); tolerate ISO too.
function lineDateISO(b) {
  const d = b.date ?? b.treatment_date
  if (d == null) return ''
  // epoch → the Bangkok treatment day, not the browser's (a 00:00–07:00 BKK visit
  // otherwise lands on the previous day for a UTC viewer and falls out of the range)
  if (typeof d === 'number' || /^\d{10,}$/.test(String(d))) return toISODate(d)
  return String(d).slice(0, 10)
}
function lineJoin(b) {
  return {
    treatmentId: b.treatment_id ?? null,
    patientId: b.patient_id ?? null,
    patientName: stripStars(b.fullname ?? ''),
    hn: b.HN ?? '',
    date: lineDateISO(b),
    branchId: b.branch_id != null ? String(b.branch_id) : '',
    doctorId: b.doctor_id != null ? String(b.doctor_id) : '',
    payer: bPayer(b),
  }
}

/* -------------------------------------------------------------------------- */
export default function Reimbursement() {
  const today = useMemo(() => todayISO(), [])   // Bangkok, not the browser clock
  const monthStart = useMemo(() => { const d = parseISO(today); return toISO(new Date(d.getFullYear(), d.getMonth(), 1)) }, [today])

  // server filters (bound the fetch) — persisted
  const [start, setStart] = usePersistedState('reim:start', monthStart)
  const [end, setEnd] = usePersistedState('reim:end', today)
  const [branchId, setBranchId] = usePersistedState('reim:branch', '0')
  const [doctorId, setDoctorId] = usePersistedState('reim:doctor', '0')
  // client filters (over loaded claim lines)
  const [payer, setPayer] = usePersistedState('reim:payer', '')
  const [statusF, setStatusF] = usePersistedState('reim:status', 'all')
  const [q, setQ] = useState('')
  const [sortKey, setSortKey] = usePersistedState('reim:sort', 'date')
  const [sortDir, setSortDir] = usePersistedState('reim:sortDir', 'desc') // 'asc' | 'desc'
  const [groupByDate, setGroupByDate] = usePersistedState('reim:groupDate', true)
  const [perPage, setPerPage] = usePersistedState('reim:perPage', 100)
  // ephemeral: the hub unmounts this panel on a tab switch; losing the page or a 40-row
  // selection mid-ticking would be a real cost. The 11 reim:* filters are already persisted.
  const [page, setPage] = useEphemeralState('reim:page', 1)
  const [groupsOpen, setGroupsOpen] = usePersistedState('reim:groupsOpen', () => new Set(PAYER_GROUPS.map((g) => g.key)))

  const me = useMe()
  const [branches, setBranches] = useState([])
  const [doctors, setDoctors] = useState([])
  const branchMap = useMemo(() => new Map(branches.map((b) => [b.id, b.name])), [branches])
  const doctorMap = useMemo(() => new Map(doctors.map((d) => [d.id, d])), [doctors])

  /* summary = the whole page's money, straight from Xano api 639. `rows` = claim lines
     for ONE payer, fetched on demand; `loaded` says which payer they are (null = none,
     '' = all). Keeping those two separate is what lets the page render before — and
     without — any lines at all. */
  const [summary, setSummary] = useState({ totals: [], scope: [] })
  const [rows, setRows] = useState([])            // [{ b, j }] active claim lines for `loaded`
  const [loaded, setLoaded] = useState(null)      // null | '' (all payers) | payer name
  /* ⚠️ `loading` BLANKS สรุปยอด AND the ledger under it; `refreshing` blanks nothing.
     Until 2026-09-14 every re-read set `loading`, so a colleague's tick-off wiped the
     table you were reading and replaced it with a spinner for as long as api 639 took —
     which, exactly when other people are working, is the 18–21s measured above. Only a
     SCOPE CHANGE may set `loading` now: that is the one case where what is on screen
     answers a different question and showing it would be a lie. `at` = when the figures
     on screen were last confirmed by the server. */
  const [state, setState] = useState({ loading: true, refreshing: false, err: '', at: 0 })   // summary
  const [lines, setLines] = useState({ loading: false, err: '' })  // ledger

  const [action, setAction] = useState({ busy: false, err: '', ok: '', progress: '' })
  const [pdfBusy, setPdfBusy] = useState('')
  /* Which ledger row you opened last. The row opens in a NEW TAB, so this page never
     unmounts and there is no "back" to land on — without a mark, coming back to a
     200-row page means hunting for where you were. Persisted so it also survives a
     reload; keyed by billing id. */
  const [lastOpened, setLastOpened] = usePersistedState('reim:lastOpened', null)

  /* ⚠️ Branch names arrive from Xano AS-IS — never relabel สนามเป้า → อารีย์ here.
     Branch 1 is สนามเป้า and branch 8 is อารีย์: two live, separately-billing branches.
     Rewriting 1's name put TWO อารีย์ entries in the สาขา filter with no way to tell
     which one you were scoping to, and made every ledger line read อารีย์ regardless
     of where the treatment actually happened. */
  useEffect(() => {
    cachedGet('/api/branches').then((r) => setBranches(asList(r).map((b) => ({ id: String(b.id ?? b.branch_id), name: b.name || b.branch_name || `สาขา #${b.id}` })))).catch(() => {})
    cachedGet('/api/doctors').then((r) => setDoctors(asList(r).map((d) => ({ id: String(d.id ?? d.doctor_id), name: stripStars(d.name || d.nickname || d.name_eng || `หมอ #${d.id}`), img: d.profile_img || d.image || '' })))).catch(() => {})
  }, [])

  /* ── LOAD 1 of 2: the summary. Cheap, always, on every scope change. ──────────
     One aggregate request answers the entire สรุปยอดตามผู้จ่าย table. Nothing here
     depends on a single claim line having been fetched, which is the whole point:
     the page paints its money before it has any lines at all. */
  const scope = useMemo(
    // end_date goes out AS PICKED — Xano applies the +1 day itself (`var $end_date`,
    // identical in api 179 / 569 / 639 / 640). Adding a day here too pulled in the next day.
    () => ({ start_date: start, end_date: end, branch_id: branchId, doctor_id: doctorId }),
    [start, end, branchId, doctorId],
  )
  const sumReq = useRef(0)
  /* ⚠️ A SUPERSEDED READ IS CANCELLED, not merely ignored. Picking a date range fires one
     of these per change, api 639 can sit there for 20s under load, and apiGet holds one
     of the browser's six concurrency slots until the BODY is read — so three abandoned
     summary reads used to sit on half the page's request budget and delay the ledger
     that replaced them. The request-id guard below still stands: it decides what may
     touch state, this decides what stays on the wire. */
  const sumAbort = useRef(null)
  const loadSummary = useCallback(async ({ silent = false } = {}) => {
    const id = ++sumReq.current
    sumAbort.current?.abort()
    const ac = new AbortController()
    sumAbort.current = ac
    setState((s) => (silent ? { ...s, refreshing: true } : { loading: true, refreshing: false, err: '', at: s.at }))
    try {
      const r = await apiGet('/api/billing/reimbursement-summary', scope, { signal: ac.signal })
      if (id !== sumReq.current) return
      setSummary({ totals: asList(r?.totals), scope: asList(r?.scope) })
      setState({ loading: false, refreshing: false, err: '', at: Date.now() })
    } catch (e) {
      if (e?.name === 'AbortError' || id !== sumReq.current) return   // we cancelled it on purpose
      /* ⚠️ A failed BACKGROUND re-read must not replace a good table with an error box.
         What is on screen is still the last answer the server gave for this scope; the
         next refresh (or any filter change) tries again. A failed FIRST load has nothing
         to protect, so that one still reports. */
      setState((s) => (silent ? { ...s, refreshing: false } : { loading: false, refreshing: false, err: String(e.message || e), at: s.at }))
    }
  }, [scope])

  /* ── The refresh scheduler: one request per burst, never all tabs at once ──────────
     Callers say how urgent their reason is (REFRESH_* above) and the earliest deadline
     wins, so ten broadcasts in ten seconds cost ONE aggregate. A hidden tab does not
     spend a request at all — it remembers that it is stale and re-reads when someone
     looks at it again. */
  const loadSummaryRef = useRef(loadSummary)
  loadSummaryRef.current = loadSummary
  const refreshTimer = useRef(null)
  const refreshDue = useRef(0)
  const refreshStale = useRef(false)

  const runRefresh = useCallback(() => {
    refreshTimer.current = null
    refreshDue.current = 0
    if (typeof document !== 'undefined' && document.hidden) { refreshStale.current = true; return }
    refreshStale.current = false
    loadSummaryRef.current({ silent: true })
  }, [])

  const scheduleRefresh = useCallback((delay) => {
    const at = Date.now() + delay + Math.random() * REFRESH_JITTER_MS
    if (refreshTimer.current && refreshDue.current <= at) return   // an earlier one already covers this
    clearTimeout(refreshTimer.current)
    refreshDue.current = at
    refreshTimer.current = setTimeout(runRefresh, Math.max(0, at - Date.now()))
  }, [runRefresh])

  const refreshNow = useCallback(() => {
    clearTimeout(refreshTimer.current)
    refreshTimer.current = null
    refreshDue.current = 0
    refreshStale.current = false
    loadSummaryRef.current({ silent: true })
  }, [])

  useEffect(() => () => { clearTimeout(refreshTimer.current); sumAbort.current?.abort() }, [])
  useEffect(() => {
    const onVis = () => { if (!document.hidden && refreshStale.current) { refreshStale.current = false; loadSummaryRef.current({ silent: true }) } }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  /* A scope change invalidates the lines too — they were fetched for the OLD window,
     and a stale ledger under a fresh summary is the one failure mode that looks like
     working software. Any refresh still queued was queued for the old window: drop it. */
  useEffect(() => {
    clearTimeout(refreshTimer.current); refreshTimer.current = null; refreshDue.current = 0; refreshStale.current = false
    setRows([]); setLoaded(null); setLines({ loading: false, err: '' })
    loadSummary()
  }, [loadSummary])

  /* ── LOAD 2 of 2: the lines, for ONE payer, only when asked. ──────────────────
     `loaded` records what the rows in state actually cover: null = nothing, '' = every
     payer, or a payer name. Anything else on screen is derived from the summary. */
  const linesReq = useRef(0)
  /* Fetch only — touches no page state, so the per-payer PDF can pull one payer's lines
     without hijacking whatever ledger is on screen. `payerName` empty = every payer.
     api 640 already carries advance_status/_received_at/_received_by/_received_amount,
     so there is no second status request to merge in any more — and a `partial` line
     finally keeps its settled amount across a reload. */
  const fetchLines = useCallback(async (payerName, signal) => {
    const all = []
    for (let page = 1; page <= MAX_PAGES_PER_PAYER; page++) {
      const r = await apiGet('/api/billing/reimbursement-lines', {
        ...scope, advancepay: payerName || undefined, per_page: 500, page,
      }, { signal })
      const items = asList(r)
      all.push(...items)
      const pageTotal = Number(r?.pageTotal ?? 1) || 1
      if (page >= pageTotal || items.length < 500) break
    }
    return all.filter(bIsActive).map((b) => ({ b, j: lineJoin(b) }))
  }, [scope])

  /* Same cancellation rule as the summary, and it matters more here: a superseded walk
     keeps asking for up to six 500-row pages of a payer nobody is looking at any more.
     ⚠️ The PDF export calls fetchLines WITHOUT a signal on purpose — its fetch belongs to
     the export, not to whatever the ledger is showing, and clicking a payer mid-export
     must not cancel the document. */
  const linesAbort = useRef(null)
  const loadLines = useCallback(async (payerName) => {
    const id = ++linesReq.current
    linesAbort.current?.abort()
    const ac = new AbortController()
    linesAbort.current = ac
    setLines({ loading: true, err: '' })
    try {
      const built = await fetchLines(payerName, ac.signal)
      if (id !== linesReq.current) return   // a newer request (or a scope change) won
      setRows(built)
      setLoaded(payerName)
      setLines({ loading: false, err: '' })
    } catch (e) {
      if (e?.name === 'AbortError' || id !== linesReq.current) return
      setLines({ loading: false, err: String(e.message || e) })
    }
  }, [fetchLines])
  useEffect(() => () => linesAbort.current?.abort(), [])

  // Picking a payer IS the request to see its ledger. ทุกผู้จ่าย deliberately loads
  // nothing — that is the 13–22s the rewrite exists to remove; there is an explicit
  // button for it instead.
  useEffect(() => {
    if (!payer) return
    if (loaded === payer || loaded === '') return
    loadLines(payer)
  }, [payer, loaded, loadLines])

  // the bulk-action notice is transient: any filter change clears it, and a
  // success message fades on its own so it can never outlive what it describes.
  useEffect(() => { setAction((a) => (a.ok || a.err ? { ...a, ok: '', err: '' } : a)) }, [start, end, branchId, doctorId, payer, statusF, q, sortKey, sortDir, page])
  useEffect(() => {
    if (!action.ok) return
    const t = setTimeout(() => setAction((a) => ({ ...a, ok: '' })), 6000)
    return () => clearTimeout(t)
  }, [action.ok])

  // Realtime: someone ELSE reconciling this payer batch. Two people work this page —
  // one reading the bank statement, one ticking lines — and without this the second
  // one keeps marking rows the first already marked.
  //
  // The broadcast carries the DELTA ({billing_ids, status, advance_received_at}), so
  // this PATCHES THE ROWS IN PLACE. Do not turn it into a refetch: reloading this page
  // is the dashboard call + one paginated call per payer + the status call, i.e. 10-20
  // requests, which would cost far more than the write being reported. Ids we have not
  // loaded (other branch, other month) simply don't match and are ignored.
  //
  // ⚠️ AND IT MUST NOT SHOW A SPINNER. This handler runs in every open tab at the same
  // instant; the aggregate re-read it asks for is scheduled, silent and jittered for
  // exactly that reason — see REFRESH_* and `state.refreshing` above.
  useXanoRealtime('billing_advance_update', (msg) => {
    const p = (msg && msg.payload) || {}
    // Xano wraps our object under payload.data on some channels; fall back to p itself.
    const d = (p && typeof p.data === 'object' && p.data) ? p.data : p
    if (d?.type && d.type !== 'advance_status') return
    const emp = d?.employee_id
    if (emp != null && me?.employee_id != null && String(emp) === String(me.employee_id)) return // our own write — already applied
    const ids = Array.isArray(d?.billing_ids) ? d.billing_ids : []
    if (!ids.length) return
    /* ⚠️ Accept the value the broadcast actually carries. Collapsing anything that is
       not `received` to `pending` made this screen silently DISAGREE with the database
       the moment a colleague marked a line partial or rejected — the row would show
       ค้างรับ until a full reload, and it would be un-ticked from their selection as if
       handled. Unknown strings still fall back to pending rather than rendering blank. */
    const status = CLAIM_STATES.has(String(d.status)) ? String(d.status) : 'pending'
    // Only `received` and `partial` carry a date; a revert and a write-off clear it.
    const at = (status === 'received' || status === 'partial')
      ? (d.advance_received_at ?? Date.now())
      : null
    const idset = new Set(ids.map(Number))
    // Does this touch a line we are actually showing? It decides how urgently the money
    // needs re-reading, so it is answered here rather than inside the state updater.
    const touchesUs = rows.some((r) => idset.has(Number(r.b.id)))
    if (touchesUs) {
      setRows((rs) => rs.map((r) => (idset.has(Number(r.b.id))
        ? { ...r, b: { ...r.b, advance_status: status, advance_received_at: at, advance_received_by: emp ?? r.b.advance_received_by } }
        : r)))
    }
    /* The headline ค้างรับ/รับแล้ว are SQL sums, so patching rows does not move them —
       only a re-read does. It is QUEUED, not fired: promptly when the change is to a
       line on screen, lazily when it is to a payer or a month we are not showing (the
       ledger is right either way, and the 25s is what stops ten people reconciling
       ten payers from each costing this tab a request). */
    scheduleRefresh(touchesUs ? REFRESH_HIT_MS : REFRESH_IDLE_MS)
  })

  // resolve branch/doctor names (maps may load after rows)
  const branchName = (jid) => branchMap.get(jid) || (jid ? `สาขา #${jid}` : '—')
  const doctorOf = (jid) => doctorMap.get(jid) || { name: jid ? `หมอ #${jid}` : '—', img: '' }

  /* ── สรุปยอดตามผู้จ่าย, derived from api 639's aggregate ─────────────────────────
     ⚠️ NOT from `rows`. The lines for most payers are not loaded, and summing the ones
     that are would report a fraction of the money as if it were the total. `totals`
     carries one row per (payer, status): a `received` row's whole claim arrived, a
     `partial` row's advance_received_amount did, and everything else is still owed.
     Same rules as bGot/bOwed/bShortfall above — change one, change the other.

     ⚠️ The ค้นหาคนไข้/HN box does NOT narrow this table any more. It could not: the
     names live on lines this page has deliberately not fetched. It is an accounting
     total for the chosen window, and it now stays put while you look someone up. */
  const payerSummary = useMemo(() => {
    const map = new Map()
    const seat = (label) => {
      const key = String(label ?? '').trim() || 'ไม่ระบุผู้จ่าย'
      if (!map.has(key)) map.set(key, { key, cases: 0, patients: new Set(), advance: 0, pending: 0, received: 0, written_off: 0, shortfall: 0, group: payerGroupKey(key) })
      return map.get(key)
    }
    for (const t of summary.totals) {
      const s = seat(t.label)
      const raw = String(t.status ?? '').toLowerCase()
      const st = CLAIM_STATES.has(raw) ? raw : 'pending'   // "" = never reconciled = ค้างรับ
      const a = num(t.advance)
      const got = st === 'received' ? a : st === 'partial' ? num(t.received_amount) : 0
      s.advance += a
      s.received += got
      if (st === 'pending') s.pending += a
      else if (st === 'rejected') s.written_off += a
      else if (st === 'partial') s.shortfall += Math.max(0, a - got)
    }
    // เคส/คนไข้ are DISTINCT counts, which an aggregate eval cannot express — `scope` is
    // one row per (payer, patient, treatment) precisely so they can be counted here.
    for (const r of summary.scope) {
      const s = seat(r.label)
      s.cases += 1
      if (r.patient_id != null) s.patients.add(r.patient_id)
    }
    const arr = [...map.values()].map((s) => ({ ...s, patients: s.patients.size }))
    arr.sort((x, y) => y.advance - x.advance)
    return arr
  }, [summary])
  const maxPayer = payerSummary.reduce((m, p) => Math.max(m, p.advance), 0) || 1
  const grandAdvance = payerSummary.reduce((a, p) => a + p.advance, 0)

  const groupSummary = useMemo(() => {
    const out = []
    for (const g of PAYER_GROUPS) {
      const payers = payerSummary.filter((p) => p.group === g.key)
      if (!payers.length) continue
      const agg = payers.reduce((a, p) => ({ cases: a.cases + p.cases, patients: a.patients + p.patients, advance: a.advance + p.advance, pending: a.pending + p.pending, received: a.received + p.received }), { cases: 0, patients: 0, advance: 0, pending: 0, received: 0 })
      out.push({ ...g, ...agg, payers })
    }
    out.sort((a, b) => b.advance - a.advance)
    return out
  }, [payerSummary])
  const maxGroup = groupSummary.reduce((m, g) => Math.max(m, g.advance), 0) || 1
  const toggleGroup = (k) => setGroupsOpen((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n })

  // ledger = loaded rows + payer + status + ค้นหา, sorted. This is the only place the
  // search box applies now — see the note on payerSummary.
  const ledger = useMemo(() => {
    let out = rows
    const needle = q.trim().toLowerCase()
    if (needle) out = out.filter(({ j }) => `${j.patientName} ${j.hn}`.toLowerCase().includes(needle))
    if (payer) out = out.filter(({ j }) => j.payer === payer)
    if (statusF !== 'all') out = out.filter(({ b }) => bStatus(b) === statusF)
    const arr = [...out]
    const dcmp = (a, b) => (a === b ? 0 : a > b ? 1 : -1) // ascending; works for epoch-ms or ISO
    const dir = sortDir === 'asc' ? 1 : -1
    arr.sort((A, B) => {
      let cmp
      if (sortKey === 'advance') cmp = bAdvance(A.b) - bAdvance(B.b)
      else if (sortKey === 'payer') cmp = A.j.payer.localeCompare(B.j.payer, 'th')
      else cmp = dcmp(A.j.date, B.j.date)
      if (cmp === 0 && sortKey !== 'date') cmp = dcmp(A.j.date, B.j.date) // stable tiebreak by date
      return cmp * dir
    })
    return arr
  }, [rows, q, payer, statusF, sortKey, sortDir])
  const ledgerSums = useMemo(() => ledger.reduce((a, { b }) => {
    a.advance += bAdvance(b)
    a.received += bGot(b)
    a.pending += bOwed(b)            // ⚠️ the headline ค้างรับ figure — rejected excluded
    a.written_off += bStatus(b) === 'rejected' ? bAdvance(b) : 0
    a.shortfall += bShortfall(b)
    return a
  }, { advance: 0, pending: 0, received: 0, written_off: 0, shortfall: 0 }), [ledger])

  // client pagination over the filtered+sorted ledger (rendering 2000+ rows is what makes it slow)
  const pageCount = Math.max(1, Math.ceil(ledger.length / perPage))
  const safePage = Math.min(Math.max(1, page), pageCount)
  const pageRows = useMemo(() => ledger.slice((safePage - 1) * perPage, safePage * perPage), [ledger, safePage, perPage])
  const firstRow = ledger.length ? (safePage - 1) * perPage + 1 : 0
  const lastRow = Math.min(safePage * perPage, ledger.length)

  // Day grouping over the VISIBLE page. Only meaningful while the ledger is date-sorted —
  // under any other sort the dates interleave and every "group" would be one row.
  // A date straddling a page boundary simply gets its header repeated on the next page.
  const grouped = groupByDate && sortKey === 'date'
  const pageGroups = useMemo(() => {
    if (!grouped) return []
    const out = []
    let cur = null
    for (const r of pageRows) {
      if (!cur || cur.date !== r.j.date) { cur = { date: r.j.date, rows: [], advance: 0, received: 0 }; out.push(cur) }
      cur.rows.push(r)
      cur.advance += bAdvance(r.b)
      cur.received += bGot(r.b)
    }
    return out
  }, [grouped, pageRows])
  useEffect(() => { setPage(1) }, [payer, statusF, q, sortKey, sortDir, perPage, start, end, branchId, doctorId])

  /* ── ❌ REMOVED 2026-09-15: the checkboxes and ทำเครื่องหมายว่ารับแล้ว / กลับเป็นค้างรับ ──
     Owner's decision. `advance_status` on the received/pending axis is now written ONLY by
     the ตามเบิก import link, so that the two systems cannot disagree about the same line.

     ⚠️ WHY THIS AXIS SPECIFICALLY, AND WHY IT WAS THE DANGEROUS ONE. A manual `received`
     here is absorbed SILENTLY by the importer: link.js re-reads the line live, finds it
     already received, and downgrades itself to `already_received` or `date_fix` — the
     latter then re-stamping `advance_received_by` with whoever clicked and overwriting the
     real transfer date with the file's. Nobody is told. Measured: billing #1337194 was put
     back to `received` by a person on 14 ก.ย. and the run page showed the stale value for
     four days (commit e24e015), because api 570 writes no billing.log[] entry at all.

     ปิดรายการ (partial / rejected) DELIBERATELY SURVIVES, and it is not an inconsistency:
     the importer REFUSES a closed line loudly (`line_closed`, link.js:274) rather than
     absorbing it, so it cannot corrupt anything — worst case it blocks a link until
     somebody reopens. And สปสช. has no importer at all (฿97,500 ค้างรับ), so removing it
     would leave that payer with no way to ever close a claim.

     The selection also drove ยอดรวมที่เลือก and nothing else — the CSV and PDF exports read
     the filtered ledger, not the selection, so both are unaffected. */
  /* ── ปิดรายการ: partial / rejected ───────────────────────────────────────────
     ⚠️ ONE LINE AT A TIME, and never from a bulk action. `rejected` is the clinic
     accepting it will never see this money; `partial` closes a claim below the amount
     billed. Both are judgements about a specific claim, so both cost one deliberate
     click and carry either an amount or a reason. No importer can reach this path —
     /api/billing/write-off is the only route that writes these values and every
     automation goes through /api/billing/status instead.

     ⚠️ THIS IS NOW THE ONLY advance_status WRITE LEFT ON THIS PAGE, and it survived the
     2026-09-15 lock-down deliberately. See the note above the removed selection block:
     the importer REFUSES a closed line loudly instead of absorbing it, so this cannot put
     the two systems out of step the way a manual `received` silently could. */
  // ⚠️ GM/Admin (2026-09-14), enforced in functions/api/billing/write-off.js — this only
  // shapes the screen. `maySetStatus` now gates the explanatory notice rather than any
  // control: since 2026-09-15 this page writes received/pending nowhere at all.
  const maySetStatus = canSetClaimStatus(me)
  // ⚠️ ADMIN ONLY, and deliberately NOT `maySetStatus` any more (owner, 2026-09-15).
  // Two people hold admin; five hold gm. Enforced in functions/api/billing/write-off.js.
  const mayWriteOff = canWriteOffClaim(me)
  const [writeOff, setWriteOff] = useState(null)   // { b, j, mode, amount, reason, date }
  const [woBusy, setWoBusy] = useState(false)
  const [woErr, setWoErr] = useState('')

  async function submitWriteOff() {
    if (!writeOff || woBusy) return
    setWoBusy(true); setWoErr('')
    try {
      const payload = writeOff.mode === 'partial'
        ? { billing_id: writeOff.b.id, status: 'partial', received_amount: Number(writeOff.amount), received_at: writeOff.date || undefined }
        : writeOff.mode === 'received'
          // Admin force รับแล้ว (2026-10-16) — one line, date + reason, logged by api 631.
          ? { billing_id: writeOff.b.id, status: 'received', received_at: writeOff.date, reason: writeOff.reason }
          : { billing_id: writeOff.b.id, status: 'rejected', reason: writeOff.reason }
      const r = await postJson('/api/billing/write-off', payload)
      const st = writeOff.mode
      const id = writeOff.b.id
      setRows((rs) => rs.map((x) => (x.b.id === id
        ? { ...x, b: { ...x.b, advance_status: st, advance_received_amount: r?.advance_received_amount ?? null, advance_received_at: r?.advance_received_at ?? null } }
        : x)))
      setWriteOff(null)
      scheduleRefresh(REFRESH_OWN_MS)   // ค้างรับ/รับแล้ว are SQL sums — a row patch alone leaves them stale
      setAction({ busy: false, err: '', ok: st === 'partial' ? 'บันทึกรับบางส่วนแล้ว' : st === 'received' ? 'บังคับเป็นรับแล้ว' : 'ตัดเป็นเบิกไม่ได้แล้ว', progress: '' })
    } catch (e) {
      setWoErr(e?.body?.detail || e?.body?.error || String(e?.message || e))
    } finally {
      setWoBusy(false)
    }
  }

  function exportCsv() {
    const head = ['วันที่', 'HN', 'คนไข้', 'สาขา', 'หมอ', 'รายการ', 'เลขที่เคลม', 'ผู้จ่าย', 'เบิก', 'คลินิก', 'หมอ', 'รวม', 'สถานะ', 'วันที่รับ']
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s }
    const lines = ledger.map(({ b, j }) => [
      j.date, j.hn, j.patientName, branchName(j.branchId), doctorOf(j.doctorId).name, bTypeName(b), bClaimRef(b), j.payer,
      bAdvance(b), bClinic(b), bDoctor(b), bTotal(b),
      STATUS_LABEL[bStatus(b)] || 'ค้างรับ', dayISO(bReceivedAt(b)),
    ].map(esc).join(','))
    const csv = '﻿' + [head.join(','), ...lines].join('\r\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
    const a = document.createElement('a')
    // Name what the file actually holds: the ledger covers `payer` if one is chosen,
    // otherwise whatever `loaded` was fetched for ('' = every payer).
    const tag = payer || (loaded || '')
    a.href = url; a.download = `reimbursement_${start}_${end}${tag ? '_' + tag : ''}.csv`
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
  }

  /* The PDF is per payer and needs that payer's LINES, which the page no longer keeps
     for everyone. */
  async function exportPdf(payerName) {
    setPdfBusy(payerName); setAction({ busy: false, err: '', ok: '' })
    try {
      let forPayer = ledger.filter(({ j }) => j.payer === payerName)
      // Not on screen? Pull just this payer — one ~1s request — WITHOUT disturbing the
      // ledger the operator is looking at.
      if (!forPayer.length) forPayer = await fetchLines(payerName)
      if (!forPayer.length) { setPdfBusy(''); return }
      const rowsData = forPayer.map(({ b, j }) => ({ date: j.date, hn: j.hn, patient: j.patientName, branch: branchName(j.branchId), doctor: doctorOf(j.doctorId).name, item: bTypeName(b), claimRef: bClaimRef(b), advance: bAdvance(b), status: STATUS_LABEL[bStatus(b)] || 'ค้างรับ' }))
      const total = forPayer.reduce((a, { b }) => a + bAdvance(b), 0)
      const r = await postJson('/api/documents/generate', { template: 'reimbursement_summary', data: { payer: payerName, start, end, rows: rowsData, total, count: forPayer.length } })
      const link = r?.url || r?.downloadUrl
      if (link) window.open(link, '_blank', 'noopener')
      else setAction({ busy: false, err: 'สร้าง PDF แล้วแต่ไม่มีลิงก์กลับมา', ok: '' })
    } catch (e) {
      const s = e?.status
      setAction({ busy: false, err: (s === 404 || s === 405) ? 'ยังไม่มีเทมเพลต PDF "reimbursement_summary" ใน renderer (ดู docs/reimbursement-xano-spec.md)' : String(e.message || e), ok: '' })
    } finally { setPdfBusy('') }
  }

  const openPatient = (pid, billingId) => {
    if (!pid) return
    if (billingId != null) setLastOpened(billingId)
    // ⚠️ /treatments, not the bare index — App.jsx:249 redirects that to `info`.
    window.open(`/patient/${pid}/treatments`, '_blank', 'noopener')
  }
  /* Completeness check, now per LOADED payer: does the sum of the lines in hand match
     what the database says that payer's claims are worth? A payer past
     MAX_PAGES_PER_PAYER, or a page that failed mid-walk, shows up here instead of
     quietly under-reporting. Nothing to check while no lines are loaded. */
  const loadedExpected = useMemo(() => {
    if (loaded == null) return null
    if (loaded === '') return grandAdvance
    return payerSummary.find((p) => p.key === loaded)?.advance ?? 0
  }, [loaded, grandAdvance, payerSummary])
  const loadedActual = useMemo(() => rows.reduce((a, { b }) => a + bAdvance(b), 0), [rows])
  const mismatch = (loaded == null || lines.loading || state.loading) ? 0 : Math.abs(loadedActual - loadedExpected)

  return (
    /* Chrome (wrapper, breadcrumb, icon, h1, subtitle) now belongs to
       ReimbursementHub.jsx. The นำเข้ารายงาน ปกส. link went with it — ปกส. is a
       sibling TAB now, and that link would have pointed at a redirect back here. */
    <div>

      {/* filters */}
      <Card icon={<Search size={18} />} title="ค้นหา / กรอง" subtitle="ช่วงวันที่ · สาขา · หมอ · ผู้จ่าย · สถานะ">
        <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">ช่วงวันที่</label>
            <DateRange start={start} end={end} today={today} onStart={setStart} onEnd={setEnd} />
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">สาขา</label>
            <Select value={branchId} onChange={setBranchId} className="min-w-[140px]"><option value="0">ทุกสาขา</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</Select>
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">หมอ</label>
            <Select value={doctorId} onChange={setDoctorId} className="min-w-[150px]"><option value="0">ทุกหมอ</option>{doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</Select>
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">ผู้จ่าย</label>
            <Select value={payer} onChange={setPayer} className="min-w-[160px]"><option value="">ทุกผู้จ่าย</option>{payerSummary.map((p) => <option key={p.key} value={p.key}>{p.key}</option>)}</Select>
          </div>
          <div>
            <label className="block text-[12px] text-slate-500 mb-1">สถานะ</label>
            <Select value={statusF} onChange={setStatusF} className="min-w-[130px]"><option value="all">ทั้งหมด</option><option value="pending">ค้างรับ</option><option value="received">รับแล้ว</option><option value="partial">รับบางส่วน</option><option value="rejected">เบิกไม่ได้</option></Select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[12px] text-slate-500 mb-1">ค้นหาคนไข้ / HN</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ชื่อ หรือ HN…" className={`${INPUT} w-full`} />
          </div>
        </div>
      </Card>

      {!state.loading && mismatch > 1 && (
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-[12.5px] text-amber-700">
          <AlertTriangle size={15} /> ยอดรวมรายการที่โหลด (฿{fmt(loadedActual)}) ต่างจากยอดสรุปฝั่งเซิร์ฟเวอร์ (฿{fmt(loadedExpected)}) — บางรายการอาจโหลดไม่ครบ
        </div>
      )}

      {/* payer summary */}
      <div className="mt-4">
        <Card icon={<Landmark size={18} />} title="สรุปยอดตามผู้จ่าย" badge={payerSummary.length ? `${payerSummary.length} ผู้จ่าย` : undefined}
          subtitle="ยอดเบิก = เงินที่ผู้จ่ายภายนอกต้องจ่ายเรา · คลิกผู้จ่ายเพื่อดูรายการ · ยอดนี้คิดจากทั้งช่วงวันที่ ไม่ขึ้นกับคำค้นหา"
          right={
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl bg-slate-50 border border-slate-200 px-4 py-2">
              <SumTile label="เบิกทั้งหมด" value={grandAdvance} color={INDIGO} strong />
              <SumTile label="ค้างรับ" value={payerSummary.reduce((a, p) => a + p.pending, 0)} color="#d97706" />
              <SumTile label="รับแล้ว" value={payerSummary.reduce((a, p) => a + p.received, 0)} color="#059669" />
              {/* A background re-read no longer blanks the page, so say plainly when the
                  money on screen was last confirmed — and give a way to ask right now. */}
              <button onClick={refreshNow} disabled={state.loading || state.refreshing}
                title={state.at ? `ยอดนี้ยืนยันกับเซิร์ฟเวอร์เมื่อ ${clock(state.at)} · กดเพื่อรีเฟรช` : 'รีเฟรชยอด'}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-400 hover:border-brand-300 hover:text-brand-600 disabled:opacity-60">
                {state.refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                <span className="tabular-nums">{state.refreshing ? 'กำลังอัปเดต' : state.at ? clock(state.at) : 'รีเฟรช'}</span>
              </button>
            </div>
          }>
          {state.err ? <Notice tone="error" text={`โหลดข้อมูลไม่สำเร็จ: ${state.err}`} />
            : state.loading ? <div className="flex items-center justify-center gap-2 text-[13px] text-slate-400 h-[180px]"><Loader2 size={16} className="animate-spin" /> กำลังโหลด…</div>
              : !payerSummary.length ? <Notice tone="warn" text="ไม่พบรายการตามเบิกในช่วงและตัวกรองที่เลือก" />
                : (
                  <div className="overflow-x-auto -mx-1 px-1">
                    <table className="w-full text-[13px] table-fixed">
                      <colgroup><col /><col className="w-[64px]" /><col className="w-[64px]" /><col className="w-[120px]" /><col className="w-[200px]" /><col className="w-[110px]" /><col className="w-[110px]" /><col className="w-[70px]" /></colgroup>
                      <thead>
                        <tr className="text-[11.5px] text-slate-400 border-b border-slate-100">
                          <th className="text-left font-medium py-1.5">ผู้จ่าย</th>
                          <th className="text-right font-medium">เคส</th>
                          <th className="text-right font-medium">คนไข้</th>
                          <th className="text-right font-medium" style={{ color: INDIGO }}>ยอดเบิก</th>
                          <th className="text-left font-medium pl-3">สัดส่วน</th>
                          <th className="text-right font-medium">ค้างรับ</th>
                          <th className="text-right font-medium">รับแล้ว</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {groupSummary.map((g) => {
                          const open = groupsOpen.has(g.key)
                          return (
                            <Fragment key={g.key}>
                              <tr onClick={() => toggleGroup(g.key)} className="border-b border-slate-100 bg-slate-50/70 cursor-pointer hover:bg-slate-100/70">
                                <td className="py-2 pr-2">
                                  <span className="inline-flex items-center gap-1.5 min-w-0">
                                    <ChevronDown size={14} className={`text-slate-400 transition ${open ? '' : '-rotate-90'}`} />
                                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: g.color }} />
                                    <span className="font-semibold text-slate-800 truncate">{g.label}</span>
                                    <span className="text-[11px] text-slate-400">({g.payers.length})</span>
                                  </span>
                                </td>
                                <td className="text-right text-slate-500 tabular-nums font-medium">{g.cases}</td>
                                <td className="text-right text-slate-500 tabular-nums font-medium">{g.patients}</td>
                                <td className="text-right"><Money v={g.advance} color={INDIGO} strong /></td>
                                <td className="pl-3"><div className="h-2 rounded-full bg-slate-200/70 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.round((g.advance / maxGroup) * 100)}%`, background: g.color }} /></div></td>
                                <td className="text-right"><Money v={g.pending} color="#d97706" strong /></td>
                                <td className="text-right"><Money v={g.received} color="#059669" strong /></td>
                                <td></td>
                              </tr>
                              {open && g.payers.map((p) => {
                                const sel = payer === p.key
                                return (
                                  <tr key={p.key} onClick={() => setPayer(sel ? '' : p.key)} className={`border-b border-slate-50 cursor-pointer ${sel ? 'bg-brand-50/60' : 'hover:bg-slate-50/60'}`}>
                                    <td className="py-1.5 pr-2 truncate"><span className="inline-flex items-center gap-2 pl-6 min-w-0"><span className="text-slate-700 truncate">{p.key}</span>{sel && <span className="rounded bg-brand-100 text-brand-700 px-1.5 py-0.5 text-[10px] shrink-0">กำลังดู</span>}</span></td>
                                    <td className="text-right text-slate-400 tabular-nums">{p.cases}</td>
                                    <td className="text-right text-slate-400 tabular-nums">{p.patients}</td>
                                    <td className="text-right"><Money v={p.advance} color={INDIGO} /></td>
                                    <td className="pl-3"><div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full opacity-70" style={{ width: `${Math.round((p.advance / maxPayer) * 100)}%`, background: g.color }} /></div></td>
                                    <td className="text-right"><Money v={p.pending} color="#d97706" /></td>
                                    <td className="text-right"><Money v={p.received} color="#059669" /></td>
                                    <td className="text-right"><button onClick={(e) => { e.stopPropagation(); exportPdf(p.key) }} title="PDF สรุปตามผู้จ่าย" disabled={pdfBusy === p.key} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-1.5 py-1 text-[11px] text-slate-500 hover:border-brand-300 hover:text-brand-600 disabled:opacity-50">{pdfBusy === p.key ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}PDF</button></td>
                                  </tr>
                                )
                              })}
                            </Fragment>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
        </Card>
      </div>

      {/* ledger — nothing is fetched until a payer is chosen (or โหลดรายการทั้งหมด). */}
      {!state.loading && !state.err && !!payerSummary.length && loaded == null && !lines.loading && !lines.err && (
        <div className="mt-4">
          <Card icon={<FileText size={18} />} title="รายการเรียกเก็บ" subtitle="เลือกผู้จ่ายเพื่อดูรายการของผู้จ่ายนั้น">
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <p className="text-[13px] text-slate-500 max-w-[440px]">
                ยอดด้านบนมาจากฝั่งเซิร์ฟเวอร์ทั้งหมดแล้ว · คลิกผู้จ่ายในตารางสรุป เพื่อโหลดเฉพาะรายการของผู้จ่ายนั้น
              </p>
              <button onClick={() => loadLines('')}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-medium text-slate-700 hover:border-brand-300 hover:text-brand-600">
                <FileText size={14} /> โหลดรายการทั้งหมด ({fmt(payerSummary.reduce((a, x) => a + x.cases, 0))} เคส)
              </button>
              {/* Honest about the cost: this is the load the rewrite exists to make optional. */}
              <p className="text-[11.5px] text-slate-400">ทั้งหมดใช้เวลานานกว่า — ใช้เมื่อจะส่งออก CSV ของทุกผู้จ่าย</p>
            </div>
          </Card>
        </div>
      )}

      {!state.loading && !state.err && lines.loading && (
        <div className="mt-4">
          <Card icon={<FileText size={18} />} title="รายการเรียกเก็บ" subtitle={payer ? `ผู้จ่าย: ${payer}` : 'ทุกผู้จ่าย'}>
            <div className="flex items-center justify-center gap-2 text-[13px] text-slate-400 h-[160px]"><Loader2 size={16} className="animate-spin" /> กำลังโหลดรายการ…</div>
          </Card>
        </div>
      )}

      {!state.loading && !state.err && !lines.loading && !!lines.err && (
        <div className="mt-4">
          <Card icon={<FileText size={18} />} title="รายการเรียกเก็บ" subtitle={payer ? `ผู้จ่าย: ${payer}` : 'ทุกผู้จ่าย'}>
            <Notice tone="error" text={`โหลดรายการไม่สำเร็จ: ${lines.err}`} />
          </Card>
        </div>
      )}

      {!state.loading && !state.err && !lines.loading && !lines.err && loaded != null && !ledger.length && (
        <div className="mt-4">
          <Card icon={<FileText size={18} />} title="รายการเรียกเก็บ" subtitle={payer ? `ผู้จ่าย: ${payer}` : loaded === '' ? 'ทุกผู้จ่าย' : `ผู้จ่าย: ${loaded}`}>
            <Notice tone="warn" text={q.trim() ? `ไม่พบคนไข้ "${q.trim()}" ในรายการที่โหลด` : 'ไม่มีรายการในตัวกรองที่เลือก'} />
          </Card>
        </div>
      )}

      {!state.loading && !state.err && !lines.loading && !lines.err && !!ledger.length && (
        <div className="mt-4">
          <Card icon={<FileText size={18} />} title="รายการเรียกเก็บ" badge={`${fmt(ledger.length)} รายการ`} subtitle={payer ? `ผู้จ่าย: ${payer}` : loaded === '' ? 'ทุกผู้จ่าย' : `ผู้จ่าย: ${loaded}`}
            right={
              <div className="flex items-center gap-2">
                <Select value={sortKey} onChange={setSortKey} className="text-[12px]"><option value="date">เรียง: วันที่</option><option value="advance">เรียง: ยอดเบิก</option><option value="payer">เรียง: ผู้จ่าย</option></Select>
                {sortKey === 'date' && (
                  <button onClick={() => setGroupByDate((v) => !v)} title={groupByDate ? 'ยกเลิกจัดกลุ่มตามวันที่' : 'จัดกลุ่มตามวันที่'} className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1.5 text-[12.5px] ${groupByDate ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-600 hover:border-brand-300 hover:text-brand-600'}`}><CalendarDays size={14} /><span className="hidden sm:inline">จัดกลุ่มวันที่</span></button>
                )}
                <button onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))} title={sortDir === 'asc' ? 'น้อย → มาก / เก่า → ใหม่' : 'มาก → น้อย / ใหม่ → เก่า'} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1.5 text-[12.5px] text-slate-600 hover:border-brand-300 hover:text-brand-600">{sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}<span className="hidden sm:inline">{sortDir === 'asc' ? 'น้อย→มาก' : 'มาก→น้อย'}</span></button>
                <button onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12.5px] text-slate-600 hover:border-brand-300 hover:text-brand-600"><Download size={14} /> CSV</button>
              </div>
            }>
            {action.err && <div className="mb-3 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-[12.5px] text-rose-700"><AlertTriangle size={15} className="mt-0.5 shrink-0" /><span className="flex-1">{action.err}</span><button onClick={() => setAction((a) => ({ ...a, err: '' }))} className="shrink-0 rounded p-0.5 text-rose-400 hover:bg-rose-100 hover:text-rose-700" aria-label="ปิด"><X size={14} /></button></div>}
            {action.ok && <div className="mb-3 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-[12.5px] text-emerald-700"><CheckCircle2 size={15} /><span className="flex-1">{action.ok}</span><button onClick={() => setAction((a) => ({ ...a, ok: '' }))} className="shrink-0 rounded p-0.5 text-emerald-500 hover:bg-emerald-100 hover:text-emerald-700" aria-label="ปิด"><X size={14} /></button></div>}

            {/* ⚠️ The bulk ทำเครื่องหมายว่ารับแล้ว / กลับเป็นค้างรับ bar stood here until
                2026-09-15. It is gone on purpose and the sentence below is its replacement:
                a control that vanishes with no explanation reads as a bug or a lost
                permission, and the next question is always "who took it away". */}
            {maySetStatus ? (
              <div className="mb-3 flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[12.5px] text-slate-600">
                <Info size={15} className="mt-0.5 shrink-0 text-slate-400" />
                <span>
                  สถานะ <b>รับแล้ว / ค้างรับ</b> เปลี่ยนได้จากการ<b>ผูกรายการกับไฟล์ที่ผู้จ่ายส่งมา</b>เท่านั้น —
                  ดูที่แท็บ <b>นำเข้า ประกัน/Partner</b> · ทำแบบนี้เพื่อให้ยอดในระบบตรงกับเงินที่เข้าจริงเสมอ
                  และรู้ได้ว่าใครเป็นคนผูก
                </span>
              </div>
            ) : null}

            <div className="flex items-center justify-between mb-2 text-[12.5px]">
              <span className="text-slate-400">{fmt(ledger.length)} รายการ</span>
              <div className="flex items-center gap-3 text-slate-500">
                <span>ค้างรับ <span className="tabular-nums font-medium text-amber-600">฿{fmt(ledgerSums.pending)}</span></span>
                <span>รับแล้ว <span className="tabular-nums font-medium text-emerald-600">฿{fmt(ledgerSums.received)}</span></span>
                <span>เบิกรวม <span className="tabular-nums font-semibold text-slate-800">฿{fmt(ledgerSums.advance)}</span></span>
              </div>
            </div>

            <div className="overflow-x-auto -mx-1 px-1">
              <table className="w-full text-[13px] table-fixed">
                <colgroup><col className="w-[78px]" /><col /><col className="w-[110px]" /><col className="w-[120px]" /><col className="w-[130px]" /><col className="w-[100px]" /><col className="w-[96px]" /><col className="w-[52px]" /></colgroup>
                <thead>
                  <tr className="text-[11.5px] text-slate-400 border-b border-slate-100">
                    <th className="text-left font-medium py-1.5">วันที่</th>
                    <th className="text-left font-medium">คนไข้ / รายการ</th>
                    <th className="text-left font-medium">สาขา</th>
                    <th className="text-left font-medium">หมอ</th>
                    <th className="text-left font-medium">เลขที่เคลม</th>
                    <th className="text-right font-medium" style={{ color: INDIGO }}>เบิก</th>
                    <th className="text-center font-medium">สถานะ</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(grouped ? pageGroups : [{ date: '', rows: pageRows }]).map((g, gi) => {
                    return (
                    <Fragment key={grouped ? `${g.date || 'nodate'}-${gi}` : 'all'}>
                    {grouped && (
                      <tr className="border-b border-slate-100 bg-slate-50/70">
                        <td colSpan={8} className="py-1.5 pr-1">
                          <div className="flex items-center gap-2.5">
                            <span className="text-[12.5px] font-semibold text-slate-700">{fmtDayHead(g.date)}</span>
                            <span className="text-[11.5px] text-slate-400">{fmt(g.rows.length)} รายการ</span>
                            {g.received > 0 && <span className="text-[11.5px] text-emerald-600 tabular-nums">รับแล้ว ฿{fmt(g.received)}</span>}
                            <span className="ml-auto text-[12.5px] font-semibold tabular-nums" style={{ color: INDIGO }}>฿{fmt(g.advance)}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {g.rows.map(({ b, j }, i) => {
                    const opened = b.id != null && lastOpened === b.id
                    const doc = doctorOf(j.doctorId)
                    return (
                      <tr key={b.id ?? `${g.date}-${i}`}
                        onClick={j.patientId ? () => openPatient(j.patientId, b.id) : undefined}
                        title={j.patientId ? 'เปิดหน้าคนไข้ในแท็บใหม่' : undefined}
                        className={`border-b border-slate-50 border-l-2 ${j.patientId ? 'cursor-pointer' : ''} ${
                          opened ? 'bg-violet-50/60 border-l-violet-400 hover:bg-violet-50'
                            : 'border-l-transparent hover:bg-slate-50/50'
                        }`}>
                        {/* ปิด lives INSIDE a row that navigates, so it stops the click —
                            otherwise closing a line would also throw a patient tab open. */}
                        <td className="text-slate-600 tabular-nums">{grouped ? '' : fmtDate(j.date)}</td>
                        <td className="pr-2 truncate">
                          <PatientLink patientId={j.patientId} onOpen={() => { if (b.id != null) setLastOpened(b.id) }}><span className="text-slate-800">{j.patientName || '—'}</span>{j.hn ? <span className="text-slate-400 ml-1.5 text-[11.5px]">HN {j.hn}</span> : null}</PatientLink>
                          <span className="block text-[11px] text-slate-400 truncate">{bTypeName(b)}</span>
                        </td>
                        <td className="text-slate-500 truncate pr-2">{branchName(j.branchId)}</td>
                        <td className="pr-2"><span className="inline-flex items-center gap-1.5 min-w-0"><DoctorAvatar src={doc.img} name={doc.name} /><span className="truncate text-slate-600">{doc.name}</span></span></td>
                        <td className="pr-2 truncate text-slate-500 tabular-nums text-[12px]">{bClaimRef(b) || '—'}</td>
                        <td className="text-right"><Money v={bAdvance(b)} color={INDIGO} strong /></td>
                        <td className="text-center"><StatusBadge status={bStatus(b)} at={bReceivedAt(b)} got={bGot(b)} /></td>
                        <td className="text-right whitespace-nowrap">
                          {mayWriteOff && bStatus(b) === 'pending' && bAdvance(b) > 0 ? (
                            <button
                              onClick={(e) => { e.stopPropagation(); setWoErr(''); setWriteOff({ b, j, mode: 'received', amount: '', reason: '', date: '' }) }}
                              title="ปิดรายการ — รับแล้ว (Admin) · รับบางส่วน · เบิกไม่ได้"
                              className="inline-flex items-center rounded-md border border-slate-200 px-1.5 py-1 text-[11px] text-slate-500 hover:border-amber-300 hover:text-amber-700"
                            >ปิด</button>
                          ) : opened ? (
                            <Eye size={13} className="inline text-violet-400" title="เปิดล่าสุด" />
                          ) : null}
                        </td>
                      </tr>
                    )
                    })}
                    </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* pagination */}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-[12.5px] text-slate-500">
              <div className="flex items-center gap-2">
                <span>แสดง <span className="tabular-nums font-medium text-slate-700">{fmt(firstRow)}–{fmt(lastRow)}</span> จาก <span className="tabular-nums font-medium text-slate-700">{fmt(ledger.length)}</span></span>
                <Select value={perPage} onChange={(v) => setPerPage(Number(v))} className="text-[12px] min-w-[92px]"><option value={50}>50 / หน้า</option><option value={100}>100 / หน้า</option><option value={200}>200 / หน้า</option><option value={500}>500 / หน้า</option></Select>
              </div>
              <Pager page={safePage} pageTotal={pageCount} onChange={setPage} />
            </div>
          </Card>
        </div>
      )}

      {/* ปิดรายการ — the only surface that can write partial / rejected, and (Admin, 2026-10-16)
          a one-line forced รับแล้ว that api 631 logs. */}
      {writeOff ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
          onClick={(e) => { if (e.target === e.currentTarget && !woBusy) setWriteOff(null) }}>
          <div className="w-full max-w-[440px] rounded-2xl bg-white p-5 shadow-xl">
            <div className="text-[15px] font-semibold text-slate-900 mb-1">ปิดรายการตามเบิก</div>
            <div className="text-[12.5px] text-slate-500 mb-4">
              {writeOff.j.patientName || '—'} · {bPayer(writeOff.b)} · เรียกเก็บ{' '}
              <b className="text-slate-800">{fmt(bAdvance(writeOff.b))} ฿</b>
            </div>

            <div className="flex gap-2 mb-4">
              {[['received', 'รับแล้ว'], ['partial', 'รับบางส่วน'], ['rejected', 'เบิกไม่ได้']].map(([m, label]) => (
                <button key={m} type="button" onClick={() => setWriteOff((w) => ({ ...w, mode: m }))}
                  className={`flex-1 h-9 rounded-lg border text-[13.5px] font-medium transition ${
                    writeOff.mode === m ? 'border-brand-400 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
                  {label}
                </button>
              ))}
            </div>

            {writeOff.mode === 'partial' ? (
              <>
                <label className="block mb-3">
                  <span className="block text-[12.5px] font-medium text-slate-600 mb-1">ยอดที่ได้รับจริง (บาท)</span>
                  <input type="number" min="0" step="0.01" value={writeOff.amount}
                    onChange={(e) => setWriteOff((w) => ({ ...w, amount: e.target.value }))}
                    className="w-full h-9 rounded-lg border border-slate-300 px-3 text-[14px]" autoFocus />
                </label>
                <label className="block mb-3">
                  <span className="block text-[12.5px] font-medium text-slate-600 mb-1">วันที่เงินเข้าบัญชี</span>
                  <input type="date" value={writeOff.date}
                    onChange={(e) => setWriteOff((w) => ({ ...w, date: e.target.value }))}
                    className="h-9 rounded-lg border border-slate-300 px-3 text-[14px]" />
                </label>
                <p className="text-[12px] text-slate-500 mb-4">
                  ปิดรายการที่ยอดนี้ · ส่วนต่าง{' '}
                  <b className="text-slate-700">
                    {fmt(Math.max(0, bAdvance(writeOff.b) - (Number(writeOff.amount) || 0)))} ฿
                  </b>{' '}
                  จะไม่นับเป็นยอดค้างรับจากผู้จ่ายอีกต่อไป
                </p>
              </>
            ) : writeOff.mode === 'received' ? (
              <>
                <label className="block mb-3">
                  <span className="block text-[12.5px] font-medium text-slate-600 mb-1">วันที่เงินเข้าบัญชี</span>
                  <input type="date" value={writeOff.date} max={today}
                    onChange={(e) => setWriteOff((w) => ({ ...w, date: e.target.value }))}
                    className="h-9 rounded-lg border border-slate-300 px-3 text-[14px]" />
                </label>
                <label className="block mb-3">
                  <span className="block text-[12.5px] font-medium text-slate-600 mb-1">เหตุผล / หลักฐาน</span>
                  <textarea rows={3} value={writeOff.reason}
                    onChange={(e) => setWriteOff((w) => ({ ...w, reason: e.target.value }))}
                    placeholder="เช่น สปสช. โอนเข้าบัญชีแล้ว / ไม่มีไฟล์จากบริษัท ตรวจกับ statement แล้ว"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[13.5px]" autoFocus />
                </label>
                <p className="text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-4">
                  บังคับเป็น<b>รับแล้วเต็มจำนวน {fmt(bAdvance(writeOff.b))} ฿</b> โดย Admin ·
                  ใช้เมื่อเงินเข้าแล้วแต่ไม่มีไฟล์ให้ระบบนำเข้า · ชื่อผู้กด วันที่ และเหตุผลจะถูกบันทึกไว้ในประวัติของรายการนี้
                </p>
              </>
            ) : (
              <>
                <label className="block mb-3">
                  <span className="block text-[12.5px] font-medium text-slate-600 mb-1">เหตุผลที่เบิกไม่ได้</span>
                  <textarea rows={3} value={writeOff.reason}
                    onChange={(e) => setWriteOff((w) => ({ ...w, reason: e.target.value }))}
                    placeholder="เช่น เกินกำหนดยื่นเบิก / กรมธรรม์หมดอายุ / คุยกับบริษัทแล้วไม่จ่าย"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[13.5px]" autoFocus />
                </label>
                <p className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                  ใช้เมื่อ<b>ยอมรับแล้ว</b>ว่าจะไม่ได้รับเงิน — ยอดนี้จะหายออกจากค้างรับถาวร
                  ถ้ายังคุยกับบริษัทอยู่ ให้คงสถานะค้างรับไว้ · เหตุผลจะถูกบันทึกไว้ในประวัติของรายการนี้
                </p>
              </>
            )}

            {woErr ? <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-700 mb-3">{woErr}</div> : null}

            <div className="flex justify-end gap-2">
              <button type="button" disabled={woBusy} onClick={() => setWriteOff(null)}
                className="h-9 px-4 rounded-lg border border-slate-300 text-[13.5px] text-slate-600 hover:bg-slate-50">ยกเลิก</button>
              <button type="button" onClick={submitWriteOff}
                disabled={woBusy || (writeOff.mode === 'partial'
                  ? !(Number(writeOff.amount) > 0 && Number(writeOff.amount) < bAdvance(writeOff.b))
                  : writeOff.mode === 'received'
                    ? !(writeOff.date && writeOff.reason.trim().length >= 3)
                    : writeOff.reason.trim().length < 3)}
                className="h-9 px-4 rounded-lg bg-brand-600 text-white text-[13.5px] font-medium disabled:opacity-40 hover:bg-brand-700">
                {woBusy ? 'กำลังบันทึก…' : 'ยืนยัน'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/* ── presentational ─────────────────────────────────────────────────────────── */
function Select({ value, onChange, children, className = '' }) {
  return <select value={value} onChange={(e) => onChange(e.target.value)} className={`${INPUT} ${className}`}>{children}</select>
}
function Money({ v, color, strong }) {
  return <span className={`tabular-nums ${strong ? 'font-semibold' : ''}`} style={color && v ? { color } : undefined}>{v ? fmt(v) : '—'}</span>
}
function SumTile({ label, value, color, strong }) {
  return (
    <div className="min-w-[84px]">
      <div className="flex items-center gap-1.5 text-[11.5px] text-slate-500">{color && <span className="w-2 h-2 rounded-full" style={{ background: color }} />}{label}</div>
      <div className={`tabular-nums leading-tight ${strong ? 'text-[18px] font-semibold text-slate-900' : 'text-[15px] font-medium text-slate-700'}`}>{fmt(value)}</div>
    </div>
  )
}
/* ⚠️ ONE label table, read by the row pill, the CSV and the PDF. They used to each
   carry their own `=== 'received' ? 'รับแล้ว' : 'ค้างรับ'`, which meant a written-off
   claim exported as "ค้างรับ" — money the clinic has given up on, handed to an
   accountant as still collectable. */
const STATUS_LABEL = { received: 'รับแล้ว', pending: 'ค้างรับ', partial: 'รับบางส่วน', rejected: 'เบิกไม่ได้' }
const STATUS_STYLE = {
  received: ['bg-emerald-50 border-emerald-200 text-emerald-700', 'bg-emerald-500'],
  pending: ['bg-amber-50 border-amber-200 text-amber-700', 'bg-amber-500'],
  partial: ['bg-sky-50 border-sky-200 text-sky-700', 'bg-sky-500'],
  rejected: ['bg-slate-100 border-slate-300 text-slate-500', 'bg-slate-400'],
}

function StatusBadge({ status, at, got }) {
  const label = STATUS_LABEL[status] || STATUS_LABEL.pending
  const [box, dot] = STATUS_STYLE[status] || STATUS_STYLE.pending
  const title = status === 'received' && at ? `รับเมื่อ ${fmtDateTime(at)}`
    : status === 'partial' ? `ได้รับจริง ${Number(got || 0).toLocaleString('th-TH')} บาท — ปิดรายการแล้ว`
      : status === 'rejected' ? 'ตัดเป็นเบิกไม่ได้ — ไม่นับเป็นยอดค้างรับอีกต่อไป'
        : ''
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] ${box}`} title={title}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />{label}
      {status === 'partial' && got ? ` ${Number(got).toLocaleString('th-TH')}` : ''}
    </span>
  )
}
function DoctorAvatar({ src, name }) {
  const [err, setErr] = useState(false)
  if (src && !err) return <img src={src} alt="" onError={() => setErr(true)} className="w-6 h-6 rounded-full object-cover shrink-0 bg-slate-100 border border-slate-200" />
  return <span className="w-6 h-6 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[11px] font-semibold shrink-0">{nameInitial(name)}</span>
}
function fmtDate(iso) {
  if (!iso) return '—'
  const d = parseISO(iso)
  if (isNaN(d)) return iso
  return `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)}`
}
const TH_DOW = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']
// ISO → "พฤ 1 ก.ค. 2026" — the day-group header (ค.ศ. year, like the rest of this page)
function fmtDayHead(iso) {
  if (!iso) return 'ไม่ระบุวันที่'
  const d = parseISO(iso)
  if (isNaN(d)) return iso
  return `${TH_DOW[d.getDay()]} ${d.getDate()} ${TH_MON_ABBR[d.getMonth()]} ${d.getFullYear()}`
}
// epoch-ms | ISO → "D/M/YY HH:MM" (for the received date + time)
function fmtDateTime(v) {
  const p = (n) => String(n).padStart(2, '0')
  if (typeof v === 'number') {   // epoch → Bangkok wall clock, not the browser's
    const x = dateParts(v)
    return x ? `${x.d}/${x.m}/${String(x.y).slice(2)} ${p(x.hh)}:${p(x.mi)}` : '—'
  }
  const d = parseISO(v)
  if (!d || isNaN(d)) return '—'
  return `${d.getDate()}/${d.getMonth() + 1}/${String(d.getFullYear()).slice(2)} ${p(d.getHours())}:${p(d.getMinutes())}`
}
